/**
 * RecommendationCard component for displaying personalized recommendations.
 *
 * Shows recommendation text, explanation, category, and priority.
 */

import React, { useState } from 'react';
import { Recommendation } from '../../types';

interface RecommendationCardProps {
  recommendation: Recommendation;
}

const CATEGORY_ICONS: Record<string, string> = {
  exercise: '🏃‍♀️',
  nutrition: '🥗',
  sleep: '😴',
  stress: '🧘‍♀️'
};

const CATEGORY_COLORS: Record<string, string> = {
  exercise: 'bg-blue-100 text-blue-800',
  nutrition: 'bg-green-100 text-green-800',
  sleep: 'bg-purple-100 text-purple-800',
  stress: 'bg-pink-100 text-pink-800'
};

const PRIORITY_LABELS: Record<number, string> = {
  1: 'High Priority',
  2: 'Medium Priority',
  3: 'Low Priority',
  4: 'Optional',
  5: 'Optional'
};

const RecommendationCard: React.FC<RecommendationCardProps> = ({ recommendation }) => {
  const [showExplanation, setShowExplanation] = useState(false);

  const categoryColor = CATEGORY_COLORS[recommendation.category] || 'bg-gray-100 text-gray-800';
  const categoryIcon = CATEGORY_ICONS[recommendation.category] || '💡';
  const priorityLabel = PRIORITY_LABELS[recommendation.priority] || 'Medium Priority';

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2">
          <span className="text-2xl">{categoryIcon}</span>
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${categoryColor}`}>
            {recommendation.category.charAt(0).toUpperCase() + recommendation.category.slice(1)}
          </span>
        </div>
        {recommendation.priority === 1 && (
          <span className="text-xs font-medium px-2 py-1 rounded-full bg-red-100 text-red-800">
            {priorityLabel}
          </span>
        )}
      </div>

      <p className="text-gray-900 font-medium mb-2">{recommendation.text}</p>

      <button
        onClick={() => setShowExplanation(!showExplanation)}
        className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center"
      >
        {showExplanation ? '−' : '+'} Why this recommendation?
      </button>

      {showExplanation && (
        <div className="mt-3 p-3 bg-gray-50 rounded-md">
          <p className="text-sm text-gray-700">{recommendation.explanation}</p>
          <p className="text-xs text-gray-500 mt-2">
            Evidence: {recommendation.evidence_source.replace(/_/g, ' ')}
          </p>
        </div>
      )}
    </div>
  );
};

export default RecommendationCard;
